<script setup lang="ts">
import { BaseButton, BaseCheckbox } from '@/components/base'
import { FormDateWithTimePicker, FormInputField } from '@/components/shared'
import { AssetType, TimeFormatType } from '@/enum'
import ValueBasedTrigger from '@/models/jobs/ValueBasedTrigger'
import CONSTANTS from '@/utils/constants/string-constants'
import { containsAllValues, machineHoursRule, odometerRules, requiredRule } from '@/utils/helpers'
import { valudBasedTriggerTypes } from '@/utils/helpers/job-schedule-helpers'
import { computed, nextTick, ref, watch } from 'vue'

// UI vars
const switchButtonsHeight = '40'
const TRIGGER_LABEL = 'Whichever occurs first will be triggered'
const odometerInputRef = ref()
const machineInputRef = ref()


// props
const props = defineProps<{
  startDateLabel?: string
  readonly?: boolean
  showOdometerTrigger?: boolean
  showMachineHoursTrigger?: boolean
  showTimeBasedTrigger?: boolean
  hideScheduledDate?: boolean
  hideExpectedCompletionDate?: boolean
  showTriggerLabel?: boolean
  showCheckboxes?: boolean
  jobSheduleText?: string
  isAssetBasedJob?: boolean
  selectedAssetType?: AssetType
  hideSeparator?: boolean
}>()

// states
const jobStartDate = defineModel<any>('jobStartDate', { default: '' })
const jobDueDate = defineModel<any>('jobDueDate', { default: '' })
const odometerTrigger = defineModel<any>('odometerTrigger', { default: new ValueBasedTrigger() })
const machineHoursTrigger = defineModel<any>('machineHoursTrigger', {
  default: new ValueBasedTrigger()
})
const selectedVehicle = defineModel<any>('selectedVehicle', { default: null })
const selectedEquipment = defineModel<any>('selectedEquipment', { default: null })
const selectedMachine = defineModel<any>('selectedMachine', { default: null })
const isOdometerBasedSelected = defineModel('isOdometerBasedSelected', { default: false })
const isMachineHoursBasedSelected = defineModel('isMachineHoursBasedSelected', { default: false })
const isTimeBasedSelected = defineModel('isTimeBasedSelected', { default: false })

// methods
const resetScheduledAndDueDates = () => {
  // jobStartDate.value = ''
  // jobDueDate.value = ''
}

const currentOdometerReading = computed(() => {
  return props.selectedAssetType === AssetType.VEHICLE
    ? selectedVehicle.value?.odometerReading
    : props.selectedAssetType === AssetType.MACHINE
      ? selectedMachine.value?.odometerReading
      : undefined
})

const currentMachineHours = computed(() => {
  return props.selectedAssetType === AssetType.VEHICLE
    ? selectedVehicle.value?.machineHours
    : props.selectedAssetType === AssetType.EQUIPMENT
      ? selectedEquipment.value?.machineHours
      : props.selectedAssetType === AssetType.MACHINE
        ? selectedMachine.value?.machineHours
        : undefined
})


const handleOdometerReadingChange = () => {
  odometerTrigger.value = new ValueBasedTrigger()
  odometerInputRef.value.resetValidation()
}
const resetMachineHoursReading = () => {
  machineHoursTrigger.value = new ValueBasedTrigger()
  machineInputRef.value.resetValidation()
}


const handleScheduleAndDueDateChange = () => {
  if (!isTimeBasedSelected.value) resetScheduledAndDueDates()
}

const shouldDisableOdometerReading = computed(() => {
  const isVehicleCategorySelected = props.selectedAssetType === AssetType.VEHICLE
  const isVehicleSelected = selectedVehicle.value
  const isMachineCategorySelected = props.selectedAssetType === AssetType.MACHINE
  const isMachineSelected = selectedMachine.value
  return (
    props.isAssetBasedJob &&
    (!isVehicleCategorySelected || !isVehicleSelected) &&
    (!isMachineCategorySelected || !isMachineSelected)
  )
})

const shouldDisableMachineHoursReading = computed(() => {
  const isVehicleSelected = selectedVehicle.value
  const isEquipmentSelected = selectedEquipment.value
  const isMachineSelected = selectedMachine.value
  return props.isAssetBasedJob && !isVehicleSelected && !isEquipmentSelected && !isMachineSelected
})
watch( shouldDisableOdometerReading,(isDisabled) => {
  if (isDisabled) {
    isOdometerBasedSelected.value = false
  }
})


watch(() => odometerTrigger.value.type, () => {
  const input = odometerInputRef.value
  input?.resetValidation()
  if (isOdometerBasedSelected.value) {
    nextTick(() => input?.validate())
  }
})

watch(() => machineHoursTrigger.value.type, () => {
  const input = machineInputRef.value
  input?.resetValidation()
  if (isMachineHoursBasedSelected.value) {
    nextTick(() => input?.validate())
  }
})

</script>
<template>
  <v-divider v-if="(!hideSeparator) && (!hideScheduledDate || !hideExpectedCompletionDate)" dark />
  <!-- <span class="body-1 semibold pl-2">{{ jobSheduleText ?? CONSTANTS.JOB_SCHEDULE }}</span> -->

  <!--TRIGGER LABEL-->
  <div v-if="showTriggerLabel" class="schedule-notes px-2 py-1">
    <span class="schedule-notes-icon">
      <v-icon>mdi:mdi-information</v-icon>
    </span>
    <span>&nbsp;{{ TRIGGER_LABEL }}</span>
  </div>

  <div class="d-flex flex-column ga-8 mt-4">
    <!--JOB START AND DUE DATE-->
    <v-row v-if="!hideScheduledDate || !hideExpectedCompletionDate" class="flex-grow-0">
      <v-col v-if="showCheckboxes" class="v-col flex-grow-0">
        <BaseCheckbox
          v-model="isTimeBasedSelected"
          @update:model-value="handleScheduleAndDueDateChange"
          :disabled="readonly"
        />
      </v-col>
      <v-col v-if="!hideScheduledDate">
        <FormDateWithTimePicker
          :label="startDateLabel ?? 'Scheduled date'"
          :disabled="readonly"
          start-of-day
          hide-time
          :time-format="TimeFormatType['12 hour']"
          v-model="jobStartDate"
          :rules="jobDueDate ? [requiredRule] : []"
        />
      </v-col>
      <v-col v-if="!hideExpectedCompletionDate">
        <FormDateWithTimePicker
          label="Due date"
          :disabled="readonly"
          end-of-day
          hide-time
          :start-from="jobStartDate"
          :time-format="TimeFormatType['12 hour']"
          v-model="jobDueDate"
        />
      </v-col>
    </v-row>

    <!--ODOMETER READING-->
    <v-row v-if="showOdometerTrigger" class="d-flex">
      <v-col v-if="showCheckboxes" class="v-col flex-grow-0">
        <BaseCheckbox
          v-model="isOdometerBasedSelected"
          :disabled="readonly || shouldDisableOdometerReading"
        />
      </v-col>
      <v-col class="v-col flex-grow-0">
        <v-btn-toggle
          :multiple="false"
          v-model="odometerTrigger.type"
          :disabled="shouldDisableMachineHoursReading"
        >
          <v-btn
            v-for="(valueType, index) in valudBasedTriggerTypes"
            @click="odometerTrigger.type = valueType.value"
            :disabled="shouldDisableOdometerReading"
            :value="valueType.value"
            class="text-capitalize"
            :height="switchButtonsHeight"
            :key="index"
            :class="`border base-button ${odometerTrigger.type == valueType.value ? 'schedule-selected-days' : ''}`"
          >
            {{ valueType.title }}
          </v-btn>
        </v-btn-toggle>
      </v-col>
      <v-col class="flex-grow-0">
        <FormInputField
          ref="odometerInputRef"
          v-model.number="odometerTrigger.value"
          :disabled="shouldDisableOdometerReading"
          class="schedule-fields-min-width-compact"
          label="Distance (miles or km)"
          hide-label
          type="number"
          :rules="odometerRules(
            odometerTrigger.type,
            currentOdometerReading,
            selectedAssetType,
            isOdometerBasedSelected
          )"
        />
      </v-col>
      <v-col class="flex-grow-0">
        <BaseButton
          @click="handleOdometerReadingChange"
          :disabled="shouldDisableOdometerReading"
          border
        >
          Reset
        </BaseButton>
      </v-col>
      <v-col class="text-left">
        <div
          v-if="
            selectedAssetType === AssetType.VEHICLE &&
            selectedVehicle?.id &&
            containsAllValues(selectedVehicle?.odometerReading)
          "
          class="pt-2"
        >
          <div class="ml-2 schedule-notes px-2 py-1">
            <span class="schedule-notes-icon">
              <v-icon>mdi:mdi-information</v-icon>
            </span>
            <span class="pl-1">
              {{ CONSTANTS.CURRENT_ODOMETER_READING }}: {{ selectedVehicle.odometerReading.value }}
              {{ selectedVehicle.odometerReading.unit }}
            </span>
          </div>
        </div>
        <div
          v-if="
            selectedAssetType === AssetType.MACHINE &&
            selectedMachine?.id &&
            containsAllValues(selectedMachine?.odometerReading)
          "
          class="pt-2"
        >
          <div class="ml-2 schedule-notes px-2 py-1">
            <span class="schedule-notes-icon">
              <v-icon>mdi:mdi-information</v-icon>
            </span>
            <span class="pl-1">
              {{ CONSTANTS.CURRENT_ODOMETER_READING }}: {{ selectedMachine.odometerReading.value }}
              {{ selectedMachine.odometerReading.unit }}
            </span>
          </div>
        </div>
      </v-col>
    </v-row>

    <!--MACHINE HOURS READING-->
    <v-row v-if="showMachineHoursTrigger" class="d-flex">
      <v-col v-if="showCheckboxes" class="v-col flex-grow-0">
        <BaseCheckbox
          v-model="isMachineHoursBasedSelected"
          :disabled="readonly || shouldDisableMachineHoursReading"
        />
      </v-col>
      <v-col class="v-col flex-grow-0">
        <v-btn-toggle
          :multiple="false"
          v-model="machineHoursTrigger.type"
          :disabled="shouldDisableMachineHoursReading"
        >
          <v-btn
            v-for="(valueType, index) in valudBasedTriggerTypes"
            @click="machineHoursTrigger.type = valueType.value"
            :disabled="shouldDisableMachineHoursReading"
            :value="valueType.value"
            class="text-capitalize"
            :height="switchButtonsHeight"
            :key="index"
            :class="`border base-button ${machineHoursTrigger.type == valueType.value ? 'schedule-selected-days' : ''}`"
          >
            {{ valueType.title }}
          </v-btn>
        </v-btn-toggle>
      </v-col>
      <v-col class="flex-grow-0">
        <FormInputField
          ref="machineInputRef"
          v-model.number="machineHoursTrigger.value"
          :disabled="shouldDisableMachineHoursReading"
          class="schedule-fields-min-width-compact"
          label="Machine hours"
          type="number"
          :rules="machineHoursRule(machineHoursTrigger.type, currentMachineHours, props.selectedAssetType, isMachineHoursBasedSelected)"
        />
      </v-col>
      <v-col class="flex-grow-0">
        <BaseButton
          @click="resetMachineHoursReading"
          :disabled="shouldDisableMachineHoursReading"
          border
        >
          Reset
        </BaseButton>
      </v-col>
      <v-col class="text-left">
        <div v-if="selectedAssetType === AssetType.VEHICLE && selectedVehicle?.id && selectedVehicle.machineHours" class="pt-2">
          <div class="ml-2 schedule-notes px-2 py-1">
            <span class="schedule-notes-icon">
              <v-icon>mdi:mdi-information</v-icon>
            </span>
            <span class="pl-1"
              >{{ CONSTANTS.CURRENT_MACHINE_HOURS_READING }}:
              {{ selectedVehicle.machineHours }}</span
            >
          </div>
        </div>
        <div v-if="selectedAssetType === AssetType.EQUIPMENT && selectedEquipment?.id && selectedEquipment.machineHours" class="pt-2">
          <div class="ml-2 schedule-notes px-2 py-1">
            <span class="schedule-notes-icon">
              <v-icon>mdi:mdi-information</v-icon>
            </span>
            <span class="pl-1"
              >{{ CONSTANTS.CURRENT_MACHINE_HOURS_READING }}:
              {{ selectedEquipment.machineHours }}</span
            >
          </div>
        </div>
        <div v-if="selectedAssetType === AssetType.MACHINE && selectedMachine?.id && selectedMachine.machineHours" class="pt-2">
          <div class="ml-2 schedule-notes px-2 py-1">
            <span class="schedule-notes-icon">
              <v-icon>mdi:mdi-information</v-icon>
            </span>
            <span class="pl-1"
              >{{ CONSTANTS.CURRENT_MACHINE_HOURS_READING }}:
              {{ selectedMachine.machineHours }}</span
            >
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>
